import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import moment from "moment";
import { Channel } from "../../../domain/channel";
import { Socket } from "../../../infrastructure/socket.service";
import { ShowRepository } from "../../../domain/show-repository";
import { Subject, Subscription } from "rxjs";
import { BroadcastPresetShow, LiveShow } from "../../../domain/show";
import { ChannelRepository } from "../../../domain/channel-repository";
import {
  BroadcastModeUpdate,
  EChannelBroadcastMode,
  IChannelBroadcastModeChecker,
} from "../../../domain/broadcasts/i-channel-broadcast-mode-checker";
import { UserService } from "../../../domain/user-service";
import { Router, NavigationEnd, Event } from "@angular/router";
import { filter } from "rxjs/operators";

@Component({
  selector: "evrideo-live-broadcast-link",
  templateUrl: "./live-broadcast-link.component.html",
  styleUrls: ["./live-broadcast-link.component.scss"],
})
export class LiveBroadcastLinkComponent implements OnInit, OnDestroy {
  readonly EChannelBroadcastMode = EChannelBroadcastMode;
  public state: EChannelBroadcastMode = EChannelBroadcastMode.Off;
  private _channelBroadcastModeCheckerSubscription: Subscription;
  private _routerSubscription: Subscription;
  private _lastGlobalUpdate: BroadcastModeUpdate | null = null;
  private _lastChannelSpecificUpdate: BroadcastModeUpdate | null = null;
  public isChannelsRoute: boolean = false;

  // New properties for the enhanced functionality
  public channelId: number | null = null;
  public channelName: string | null = null;
  public nextLiveBroadcastPresetShow: BroadcastPresetShow | null = null;
  public isChannelSpecific: boolean = false;
  public isBroadcastsManager: boolean = false;

  constructor(
    private _socketService: Socket,
    private _showRepository: ShowRepository,
    private _channelRepository: ChannelRepository,
    private _channelBroadcastModeChecker: IChannelBroadcastModeChecker,
    private _userService: UserService,
    private _router: Router,
  ) {}

  get showChannelName(): boolean {
    return (
      this.isChannelsRoute &&
      this.channelName &&
      (this.state === EChannelBroadcastMode.Live ||
        this.state === EChannelBroadcastMode.PreLive)
    );
  }

  isOffState(): boolean {
    return this.state === EChannelBroadcastMode.Off;
  }

  isPreLiveState(): boolean {
    return this.state === EChannelBroadcastMode.PreLive;
  }

  isLiveState(): boolean {
    return this.state === EChannelBroadcastMode.Live;
  }

  async ngOnInit(): Promise<void> {
    // Handle route changes to detect when we're on the /channels route
    this.isBroadcastsManager =
      this._userService.getCurrentUser().isBroadcastsManager;
    this._routerSubscription = this._router.events
      .pipe(filter((event: Event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const isOnChannelsRoute =
          event.url === "/channels" || event.urlAfterRedirects === "/channels";

        // Only process if route state changed
        if (this.isChannelsRoute !== isOnChannelsRoute) {
          this.isChannelsRoute = isOnChannelsRoute;
          console.log(
            `LiveBroadcastLinkComponent-> Route changed. Is on channels route: ${this.isChannelsRoute}`,
          );

          // Apply appropriate update based on the route
          this.applyAppropriateUpdate();

          // Force recalculation of global state when navigating to channels route
          if (this.isChannelsRoute) {
            this.refreshGlobalState();
          }
        }
      });

    // Determine initial route state
    this.isChannelsRoute = this._router.url === "/channels";
    console.log(
      `LiveBroadcastLinkComponent-> Initial route. Is on channels route: ${this.isChannelsRoute}`,
    );

    // Subscribe to broadcast updates
    this._channelBroadcastModeCheckerSubscription =
      this._channelBroadcastModeChecker.modeUpdated.subscribe((update) => {
        console.log("LiveBroadcastLinkComponent-> new update: ", update);

        if (update.isGlobalUpdate) {
          this._lastGlobalUpdate = update;
        } else if (
          update.channelId &&
          update.channelId === this._userService.currentChannelId
        ) {
          this._lastChannelSpecificUpdate = update;
        } else {
          // Channel-specific update for a channel the user has not selected.
          // Ignore it so the indicator never reflects another channel's state.
          return;
        }

        this.applyAppropriateUpdate();
      });

    // Note: we deliberately do NOT subscribe to currentChannelIdUpdated here.
    // The mode-checker already subscribes to it, and synchronously emits a
    // fresh channel-specific update on modeUpdated for the new channel before
    // any subscriber added later would run. Any stale cached update is filtered
    // out by the channelId check in applyAppropriateUpdate.

    // Initial mode update
    await this.refreshState();
  }

  private async refreshState(): Promise<void> {
    if (this.isChannelsRoute) {
      await this._channelBroadcastModeChecker.updateGlobalBroadcastMode();
    } else {
      const dateTimeUtcNow = moment().utc();
      const checkPeriodEndTime = dateTimeUtcNow
        .clone()
        .add(this._userService.getCurrentUser().liveBroadcastingCheckPeriodMs);
      await this._channelBroadcastModeChecker.triggerModeUpdate(
        dateTimeUtcNow,
        checkPeriodEndTime,
      );
    }
  }

  /**
   * Apply the appropriate update (global or channel-specific) based on the
   * current route and the user's selected channel.
   *
   * Rules:
   * - On the /channels route → reflect the most relevant channel (global).
   * - Otherwise → reflect ONLY the user's currently selected channel. Never
   *   leak another channel's state into the indicator.
   */
  private async applyAppropriateUpdate(): Promise<void> {
    if (this.isChannelsRoute) {
      if (this._lastGlobalUpdate) {
        this.applyUpdate(this._lastGlobalUpdate, false);
      }
      return;
    }

    const currentChannel = await this._userService.getCurrentChannel();
    if (!currentChannel) {
      this.state = EChannelBroadcastMode.Off;
      this.channelId = null;
      this.channelName = null;
      this.nextLiveBroadcastPresetShow = null;
      this.isChannelSpecific = false;
      return;
    }

    const specific = this._lastChannelSpecificUpdate;
    if (specific && specific.channelId === currentChannel.id) {
      this.applyUpdate(specific, true);
      return;
    }

    // No fresh update for the selected channel yet — show Off for it rather
    // than falling back to another channel's state.
    this.state = EChannelBroadcastMode.Off;
    this.channelId = currentChannel.id;
    this.channelName = currentChannel.name;
    this.nextLiveBroadcastPresetShow = null;
    this.isChannelSpecific = true;
  }

  private applyUpdate(
    update: BroadcastModeUpdate,
    isChannelSpecific: boolean,
  ): void {
    this.state = update.mode;
    this.channelId = update.channelId;
    this.channelName = update.channelName;
    this.nextLiveBroadcastPresetShow = update.nextShow || null;
    this.isChannelSpecific = isChannelSpecific;
  }

  // Get the current channel ID (if any)
  private get _currentChannelId(): number | null {
    return this._userService.currentChannelId || null;
  }

  ngOnDestroy(): void {
    if (this._channelBroadcastModeCheckerSubscription) {
      this._channelBroadcastModeCheckerSubscription.unsubscribe();
    }

    if (this._routerSubscription) {
      this._routerSubscription.unsubscribe();
    }
  }

  public timeLeftForLiveBroadcastingFormatted(): string {
    const showToUse =
      this.nextLiveBroadcastPresetShow ||
      this._channelBroadcastModeChecker.nextLiveBroadcastPresetShow;

    if (showToUse) {
      const dateTimeUtcNow = moment.utc();
      const timeLeftForLiveBroadcastingMs = Math.max(
        0,
        showToUse.startTime.clone().diff(dateTimeUtcNow, "milliseconds"),
      );
      return moment.utc(timeLeftForLiveBroadcastingMs).format("HH:mm:ss");
    } else {
      return "";
    }
  }

  // Get descriptive text about the current state
  public getStateDescription(): string {
    if (this.isChannelSpecific && this.channelName) {
      // Channel-specific information
      switch (this.state) {
        case EChannelBroadcastMode.Live:
          return `${this.channelName} is LIVE now!`;
        case EChannelBroadcastMode.PreLive:
          return `${this.channelName} will be LIVE soon`;
        default:
          return `${this.channelName} is not broadcasting`;
      }
    } else {
      // Global information
      switch (this.state) {
        case EChannelBroadcastMode.Live:
          return this.channelName
            ? `${this.channelName} is LIVE now!`
            : "Broadcasting LIVE now!";
        case EChannelBroadcastMode.PreLive:
          return this.channelName
            ? `${this.channelName} will be LIVE soon`
            : "Broadcasting will be LIVE soon";
        default:
          return "No active broadcasts";
      }
    }
  }

  /**
   * Refresh the global broadcast state by triggering a mode update
   */
  private refreshGlobalState(): void {
    // We need to add this method to our IChannelBroadcastModeChecker interface first
    // For now, we'll cast to the concrete implementation to access the method
    const checker = this._channelBroadcastModeChecker as any;
    if (checker.updateGlobalBroadcastMode) {
      checker.updateGlobalBroadcastMode();
    }
  }

  /**
   * Refresh the channel-specific state when on a channel page
   */
  private refreshChannelSpecificState(): void {
    const channelId = this._currentChannelId;
    if (channelId) {
      console.log(
        `LiveBroadcastLinkComponent-> Refreshing state for channel ${channelId}`,
      );

      // Get the latest state for this channel
      const dateTimeUtcNow = moment().utc();
      const checkPeriodEndTime = dateTimeUtcNow
        .clone()
        .add(this._userService.getCurrentUser().liveBroadcastingCheckPeriodMs);

      // This will trigger an update for the current channel
      this._channelBroadcastModeChecker
        .triggerModeUpdate(dateTimeUtcNow, checkPeriodEndTime)
        .then(() => {
          console.log(
            `LiveBroadcastLinkComponent-> Channel ${channelId} state refreshed`,
          );
        });
    } else {
      // If no channel ID, fall back to the global state
      this.refreshGlobalState();
    }
  }

  /**
   * Navigate to the broadcast edit page for the current broadcast preset
   */
  public async navigateToBroadcastEdit(): Promise<void> {
    if (!this.isBroadcastsManager) {
      return;
    }
    if (!this.channelId) {
      return;
    }
    const presetUid =
      await this._channelBroadcastModeChecker.getLiveAssosiatedPresetUid(
        this.channelId,
      );
    if (presetUid) {
      this._router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this._router.navigate(["/broadcasts/live/", presetUid], {
          state: { fromLiveLink: true },
          onSameUrlNavigation: "reload",
        });
      });
    }
  }
  public async onMouseOver(): Promise<void> {
    if (!this.isChannelsRoute) {
      return;
    }
    if (
      (this.isBroadcastsManager && this.state === EChannelBroadcastMode.Live) ||
      this.state === EChannelBroadcastMode.PreLive
    ) {
      const channel = await this._channelRepository.getChannelById(
        this.channelId,
      );
      if (channel) {
        this._userService.setCurrentChannel(channel);
      }
    }
  }
}
